/**
 * Ship From Types
 *
 * Types for ship-from locations (warehouses) and their addresses.
 */

// --- Enum/Union Types ---

import type { ResidentialIndicator } from './reference-data';

export type { ResidentialIndicator };

export type InventorySourceType = 'Unknown' | 'None' | 'ThirdParty' | 'Native' | 'Store' | 'FulfillmentProvider' | 'Integrations';

export type DayOfWeek = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

// --- Pickup Settings ---

export type PickupDay = {
  shipFromContactId: string | null;
  instructions: string | null;
  day: DayOfWeek;
  startTime: string | null;
  endTime: string | null;
  preferredTime: string | null;
  pickPackTime: string | null;
};

export type PickupSettings = {
  days: PickupDay[] | null;
};

// --- Ship From Contact ---

export type ShipFromContact = {
  shipFromContactId: string;
  shipFromId: string;
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  phoneNumberExtension: string | null;
  locationNotes: string | null;
};

// --- Address Types ---

/**
 * Address nested within a ship-from location (origin or return)
 */
export type ShipFromAddress = {
  name: string;
  company: string | null;
  phone: string;
  email: string;
  line1: string;
  line2: string;
  line3: string | null;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  residentialIndicator: ResidentialIndicator;
  ianaTimeZone: string | null;
};

/** Stable singleton empty address — use as a default to avoid creating new object references on every render. */
export const EMPTY_SHIP_FROM_ADDRESS: ShipFromAddress = {
  name: '',
  company: null,
  phone: '',
  email: '',
  line1: '',
  line2: '',
  line3: null,
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  residentialIndicator: 'Unknown',
  ianaTimeZone: null,
};

// ---------------------------------------------------------------------------
// LTL Defaults — per-location freight defaults stored in dbo.WarehouseSetting
// ---------------------------------------------------------------------------

/**
 * Pickup service booleans for LTL defaults.
 * Names align with the backend FreightAccessorials contract.
 * Note: the wizard's `pickupServices` field uses `isDropAtTerminal` for the
 * frontend-facing key — the freight-payload-mappers bridge the rename to
 * `isCarrierTerminalPickup` at API time; this type uses the API-facing name.
 */
/**
 * Keys use wizard-facing names (`isDropAtTerminal`, `isLimitedAccess`) so
 * `WizardPickupServices` can be a true alias of this type.
 * The conversion to backend names (IsCarrierTerminalPickup / pickupLocationType)
 * happens in `ltl-defaults.ts` when building the API save request.
 */
export type LtlPickupServices = {
  isInsidePickup: boolean;
  isConstructionSitePickup: boolean;
  isLiftgatePickup: boolean;
  isNotifyBeforePickup: boolean;
  isResidentialPickup: boolean;
  /** Backend: IsCarrierTerminalPickup */
  isDropAtTerminal: boolean;
  isGroceryConsolidationPickup: boolean;
  /** Backend: pickupLocationType = 'LimitedAccess' */
  isLimitedAccess: boolean;
  isTradeshowPickup: boolean;
};

/**
 * LTL defaults stored per ship-from location.
 * Times are `"HH:mm:ss"` strings (24h, seller-local wall clock, no timezone).
 * `willArrangePickupSeparately` and non-null times are mutually exclusive (AC6).
 */
export type LtlDefaults = {
  /** `"HH:mm:ss"` 24h string, or null when not set. */
  pickupReadyTime: string | null;
  /** `"HH:mm:ss"` 24h string, or null when not set. */
  pickupCloseTime: string | null;
  willArrangePickupSeparately: boolean;
  pickupInstructions: string | null;
  pickupServices: LtlPickupServices;
  // pickupLocationType removed — Limited Access is stored as isLimitedAccess bool
  // in LtlPickupServices, matching WizardPickupServices.isLimitedAccess exactly.
};

/**
 * Ship from location (warehouse)
 */
export type ShipFrom = {
  shipFromId: string;
  isDefault: boolean;
  createDateUtc: string;
  name: string;
  originAddress: ShipFromAddress;
  returnAddress: ShipFromAddress;
  company: string | null;
  phone: string;
  line1: string;
  line2: string;
  line3: string | null;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  residentialIndicator: ResidentialIndicator;
  verificationStatus: string | null;
  verificationMessage: string | null;
  verificationUtc: string | null;
  lockAddress: boolean;
  isAssociatedToInventorySource: boolean;
  inventorySourceType: InventorySourceType;
  inventorySourceId: string | null;
  inventorySourceExternalId: string | null;
  pickupSettings: PickupSettings | null;
  fillProviderWarehouseId: string | null;
  /**
   * LTL (freight) defaults for this location. Absent or null when no defaults have been saved.
   * Written exclusively via PUT api/seller/shipfrom/{id}/ltl-defaults; the full ShipFrom PUT ignores it.
   */
  ltlDefaults?: LtlDefaults | null;
};

/**
 * Payload for creating a new ship-from location
 */
export type CreateShipFromPayload = {
  name: string;
  isDefault: boolean;
  originAddress: ShipFromAddress;
  returnAddress: ShipFromAddress;
};

/**
 * Payload for updating an existing ship-from location
 */
export type UpdateShipFromPayload = CreateShipFromPayload & {
  shipFromId: string;
};
